"use client";

import { CircleCheck, Info } from "lucide-react";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { useAccount } from "wagmi";
import { Button } from "@/components/ui/button";
import { useStatementVoteMine } from "@/hooks/statement/useStatementVoteMine";
import { formatDnm } from "@/lib/statement/formatDnm";
import { StatementVoteModal } from "./StatementVoteModal";

interface StatementVotePanelProps {
  statementId: string;
  index: number;
  isOpen: boolean;
}

const InfoNote: React.FC = () => {
  const t = useTranslations("statement");
  return (
    <div className="flex items-center gap-3">
      <Info className="size-5 shrink-0 text-text-secondary" aria-hidden />
      <p className="flex-1 text-sm leading-relaxed text-text-secondary">
        {t("vote.snapshotNote")}
      </p>
    </div>
  );
};

export const StatementVotePanel: React.FC<StatementVotePanelProps> = ({
  statementId,
  index,
  isOpen,
}) => {
  const t = useTranslations("statement");
  const { isConnected } = useAccount();
  const [open, setOpen] = useState(false);

  const mine = useStatementVoteMine(statementId, isConnected);
  const votes = mine.data?.data?.votes ?? [];
  const weight = mine.data?.data?.weight ?? "0";
  const hasVoted = votes.length > 0;

  if (!isOpen) return null;

  if (!isConnected) {
    return (
      <p className="text-sm text-text-secondary">{t("vote.connectRequired")}</p>
    );
  }

  return (
    <div className="space-y-3">
      <InfoNote />
      {hasVoted && (
        <div className="flex justify-center">
          <span className="inline-flex items-center gap-2 rounded-xs border border-semantic-success bg-semantic-success/15 px-4 py-2 text-sm font-semibold text-semantic-success">
            <CircleCheck className="size-5 shrink-0" aria-hidden />
            {t("vote.success", { dnm: formatDnm(weight) })}
          </span>
        </div>
      )}
      {hasVoted && (
        <div className="space-y-1 text-center text-sm text-text-secondary">
          {votes.map((vote) => (
            <p key={`${vote.source}-${vote.support}`}>
              {t(
                vote.source === "wallet"
                  ? "vote.walletLabel"
                  : "vote.stakedLabel",
                { amount: formatDnm(vote.weight) },
              )}
              {" · "}
              {t(vote.support === "yes" ? "detail.yes" : "detail.no")}
            </p>
          ))}
        </div>
      )}
      {mine.isError && (
        <p className="text-sm text-text-secondary">{t("vote.readFailed")}</p>
      )}
      {
        <div className="flex justify-center">
          <Button
            variant="solid"
            colors="primary"
            endIcon={<CircleCheck size={18} aria-hidden />}
            disabled={mine.isLoading || mine.isError}
            onClick={() => setOpen(true)}
          >
            {hasVoted ? t("vote.againCta") : t("detail.voteCta")}
          </Button>
        </div>
      }
      <StatementVoteModal
        open={open}
        onOpenChange={setOpen}
        statementId={statementId}
        index={index}
        hasVoted={hasVoted}
      />
    </div>
  );
};
